// src/permissions.ts
function matchesTool(pattern, toolName) {
  if (pattern === "*" || pattern === toolName) return true;
  if (pattern.includes("*")) {
    const escaped = pattern.replace(/[.+^${}()|[\]\\]/g, "\\$&").replace(/\*/g, ".*");
    return new RegExp(`^${escaped}$`, "i").test(toolName);
  }
  return false;
}
function matchesPath(patterns, target) {
  if (!patterns || patterns.length === 0) return true;
  const normalized = target.replace(/\\/g, "/");
  return patterns.some((p) => {
    const pat = p.replace(/\\/g, "/");
    if (pat.includes("*")) {
      const escaped = pat.replace(/[.+^${}()|[\]\\]/g, "\\$&").replace(/\*/g, ".*");
      return new RegExp(`^${escaped}$`).test(normalized) || normalized.includes(pat.replace(/\*/g, ""));
    }
    return normalized === pat || normalized.endsWith("/" + pat) || normalized.includes(pat);
  });
}
function decisionFromAction(action) {
  if (action === "allow") return "allowed";
  if (action === "deny") return "denied";
  return "requires_prompt";
}
var PermissionManager = class {
  constructor(autoApproveReads = true, rules = []) {
    this.autoApproveReads = autoApproveReads;
    this.rules = rules;
  }
  autoApproveReads;
  rules;
  setRules(rules) {
    this.rules = rules;
  }
  checkRule(toolName, target) {
    for (const rule of this.rules) {
      if (!matchesTool(rule.tool, toolName)) continue;
      if (!matchesPath(rule.paths, target)) continue;
      return decisionFromAction(rule.action);
    }
    return null;
  }
  checkRead(target = "") {
    const hit = this.checkRule("read_file", target) ?? this.checkRule("read", target) ?? this.checkRule("*", target);
    if (hit) return hit;
    return this.autoApproveReads ? "allowed" : "requires_prompt";
  }
  checkWrite(path) {
    const hit = this.checkRule("write_file", path) ?? this.checkRule("write", path) ?? this.checkRule("*", path);
    return hit ?? "requires_prompt";
  }
  checkEdit(path) {
    const hit = this.checkRule("edit_file", path) ?? this.checkRule("edit", path) ?? this.checkRule("*", path);
    return hit ?? "requires_prompt";
  }
  checkDelete(path) {
    const hit = this.checkRule("delete_file", path) ?? this.checkRule("delete", path) ?? this.checkRule("*", path);
    return hit ?? "requires_prompt";
  }
  checkExecute(cmd) {
    const hit = this.checkRule("run_command", cmd) ?? this.checkRule("execute", cmd) ?? this.checkRule("bash", cmd) ?? this.checkRule("*", cmd);
    return hit ?? "requires_prompt";
  }
  checkTool(toolName, target = "") {
    const hit = this.checkRule(toolName, target);
    if (hit) return hit;
    if (["read_file", "list_directory", "get_project_info", "get_project_structure", "search_files", "grep", "glob", "get_code_outline", "git_status", "git_diff", "semantic_search"].includes(toolName)) {
      return this.autoApproveReads ? "allowed" : "requires_prompt";
    }
    return "requires_prompt";
  }
};

// src/registry.ts
var ToolRegistry = class {
  tools = /* @__PURE__ */ new Map();
  register(tool) {
    this.tools.set(tool.name, tool);
    return this;
  }
  get(name) {
    return this.tools.get(name);
  }
  list() {
    return [...this.tools.values()];
  }
  async execute(name, input, context) {
    const tool = this.get(name);
    if (!tool) return { ok: false, error: `Unknown tool: ${name}` };
    const parsed = tool.inputSchema.safeParse(input);
    if (!parsed.success) return { ok: false, error: parsed.error.message };
    return tool.execute(parsed.data, context);
  }
};

// src/planning/plan-manager.ts
var PlanManager = class {
  pendingResolve = null;
  pendingPlan = null;
  /** Called by the CLI's readline handler when the user responds to a plan prompt. */
  hasPendingPlan() {
    return this.pendingResolve !== null;
  }
  /** Resolve the pending plan with the user's decision. */
  resolve(approved) {
    if (this.pendingResolve) {
      const fn = this.pendingResolve;
      this.pendingResolve = null;
      this.pendingPlan = null;
      fn(approved);
    }
  }
  /** Called by the create_plan tool to display and await plan approval. */
  async requestApproval(plan, onDisplay) {
    onDisplay(plan);
    return new Promise((resolve3) => {
      this.pendingPlan = plan;
      this.pendingResolve = resolve3;
    });
  }
};

// src/planning/create-plan.ts
import { z } from "zod";
var stepSchema = z.object({
  description: z.string().describe("One-line description of this step."),
  details: z.string().optional().describe("Optional technical details or notes for this step.")
});
var inputSchema = z.object({
  title: z.string().describe('Short title for the implementation plan (e.g. "Implement Stripe Payment Gateway").'),
  rationale: z.string().optional().describe("Brief explanation of the approach chosen."),
  steps: z.array(stepSchema).min(1).describe("Ordered list of implementation steps.")
});
var BOLD = "\x1B[1m";
var RESET = "\x1B[0m";
var PURPLE = "\x1B[38;2;168;85;247m";
var WHITE = "\x1B[97m";
var YELLOW = "\x1B[93m";
var DIM = "\x1B[2m";
var GREEN = "\x1B[92m";
var MIN_BOX_W = 52;
var MAX_BOX_W = 92;
function stripAnsi(text) {
  return text.replace(/\x1b\[[0-9;]*m/g, "");
}
function visibleLength(text) {
  return stripAnsi(text).length;
}
function terminalInnerWidth() {
  const cols = process.stdout.columns || 80;
  return Math.max(40, Math.min(MAX_BOX_W, cols - 4));
}
function wrapText(text, width) {
  if (width <= 0) return [text];
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [""];
  const lines = [];
  let current = "";
  for (const word of words) {
    if (word.length > width) {
      if (current) {
        lines.push(current);
        current = "";
      }
      for (let i = 0; i < word.length; i += width) {
        lines.push(word.slice(i, i + width));
      }
      continue;
    }
    const next = current ? `${current} ${word}` : word;
    if (next.length > width) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  }
  if (current) lines.push(current);
  return lines;
}
function boxBorder(char, width) {
  const line = "\u2500".repeat(width + 2);
  const close = char === "\u256D" ? "\u256E" : char === "\u2570" ? "\u256F" : "\u2524";
  return `${PURPLE}${char}${line}${close}${RESET}`;
}
function boxLine(content, width) {
  const pad = Math.max(0, width - visibleLength(content));
  return `${PURPLE}\u2502${RESET} ${content}${" ".repeat(pad)} ${PURPLE}\u2502${RESET}`;
}
function calculateWidth(title, rationale, steps) {
  const maxInner = terminalInnerWidth();
  const candidates = [
    `\u{1F4CB} Implementation Plan: ${title}`,
    rationale ?? "",
    ...steps.flatMap((step, i) => [
      `[ ] Langkah ${i + 1}: ${step.description}`,
      step.details ? `\u21B3 ${step.details}` : ""
    ])
  ].map((line) => visibleLength(line));
  return Math.min(maxInner, Math.max(MIN_BOX_W, ...candidates));
}
function formatPlan(title, rationale, steps) {
  const width = calculateWidth(title, rationale, steps);
  const lines = [];
  lines.push("");
  lines.push(boxBorder("\u256D", width));
  const titlePrefix = `${BOLD}${PURPLE}\u{1F4CB} Implementation Plan:${RESET} `;
  const titleWrapWidth = width - visibleLength("\u{1F4CB} Implementation Plan: ");
  const titleLines = wrapText(title, titleWrapWidth);
  titleLines.forEach((line, index) => {
    const prefix = index === 0 ? titlePrefix : " ".repeat(visibleLength("\u{1F4CB} Implementation Plan: "));
    lines.push(boxLine(`${prefix}${BOLD}${WHITE}${line}${RESET}`, width));
  });
  lines.push(boxBorder("\u251C", width));
  if (rationale) {
    for (const line of wrapText(rationale, width)) {
      lines.push(boxLine(`${DIM}${line}${RESET}`, width));
    }
    lines.push(boxLine("", width));
  }
  steps.forEach((step, i) => {
    const prefix = `${YELLOW}[ ]${RESET} ${BOLD}Langkah ${i + 1}:${RESET} `;
    const continuation = " ".repeat(visibleLength(`[ ] Langkah ${i + 1}: `));
    const stepWrapWidth = width - visibleLength(`[ ] Langkah ${i + 1}: `);
    wrapText(step.description, stepWrapWidth).forEach((line, index) => {
      lines.push(boxLine(`${index === 0 ? prefix : continuation}${WHITE}${line}${RESET}`, width));
    });
    if (step.details) {
      const detailPrefix = `${DIM}    \u21B3 `;
      const detailContinuation = " ".repeat(visibleLength("    \u21B3 "));
      const detailWrapWidth = width - visibleLength("    \u21B3 ");
      wrapText(step.details, detailWrapWidth).forEach((line, index) => {
        lines.push(boxLine(`${index === 0 ? detailPrefix : detailContinuation}${DIM}${line}${RESET}`, width));
      });
    }
  });
  lines.push(boxBorder("\u2570", width));
  lines.push("");
  return lines.join("\n");
}
function formatPlanApproval(stepCount) {
  const width = Math.max(
    MIN_BOX_W,
    visibleLength("\u25C6 Plan Approval Required"),
    visibleLength(`  Scope    ${stepCount} langkah siap dieksekusi`),
    visibleLength("  Approve  y"),
    visibleLength("  Cancel   Enter / n")
  );
  const lines = [];
  lines.push(boxBorder("\u256D", width));
  lines.push(boxLine(`${BOLD}${PURPLE}\u25C6 Plan Approval Required${RESET}`, width));
  lines.push(boxBorder("\u251C", width));
  lines.push(boxLine(`  ${DIM}Status${RESET}   ${YELLOW}Menunggu persetujuan${RESET}`, width));
  lines.push(boxLine(`  ${DIM}Scope${RESET}    ${WHITE}${stepCount} langkah siap dieksekusi${RESET}`, width));
  lines.push(boxLine(`  ${DIM}Approve${RESET}  ${GREEN}${BOLD}y${RESET}`, width));
  lines.push(boxLine(`  ${DIM}Cancel${RESET}   ${DIM}Enter / n${RESET}`, width));
  lines.push(boxBorder("\u2570", width));
  lines.push(`${GREEN}${BOLD}?${RESET} Approve plan? ${DIM}[y/N]${RESET} `);
  return lines.join("\n");
}
function createPlanTool(planManager) {
  return {
    name: "create_plan",
    description: "Present a step-by-step implementation plan to the user and wait for their approval before executing any code changes. MUST be called before making multiple file edits for complex tasks.",
    inputSchema,
    async execute(input) {
      const approved = await planManager.requestApproval(
        { title: input.title, rationale: input.rationale, steps: input.steps },
        (plan) => {
          process.stdout.write(formatPlan(plan.title, plan.rationale, plan.steps));
          process.stdout.write(formatPlanApproval(plan.steps.length));
        }
      );
      if (approved) {
        process.stdout.write(`
${GREEN}${BOLD}\u2713 Rencana disetujui! Memulai eksekusi...${RESET}

`);
      } else {
        process.stdout.write(`
${DIM}\u2717 Rencana dibatalkan.${RESET}

`);
      }
      return {
        ok: true,
        value: { approved, stepCount: input.steps.length }
      };
    }
  };
}

// src/filesystem/get-code-outline.ts
import { readFile } from "fs/promises";
import { resolveSafePath } from "@vexalyn/context";
import { ToolExecutionError } from "@vexalyn/shared";
import { z as z2 } from "zod";
var inputSchema2 = z2.object({
  path: z2.string().describe("The path of the code file to get the outline for.")
});
var getCodeOutlineTool = {
  name: "get_code_outline",
  description: "Retrieve a list of classes, interfaces, types, functions, and methods defined in a code file, along with their line numbers. Use this to locate code blocks before reading or editing.",
  inputSchema: inputSchema2,
  async execute(input, context) {
    try {
      const fullPath = resolveSafePath(context.workspaceRoot, input.path);
      const content = await readFile(fullPath, "utf8");
      const lines = content.split("\n");
      const symbols = [];
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (!line) continue;
        const lineNum = i + 1;
        const classMatch = line.match(/^\s*(export\s+)?(default\s+)?class\s+(\w+)/);
        if (classMatch && classMatch[3]) {
          symbols.push({ name: classMatch[3], type: "class", line: lineNum });
          continue;
        }
        const interfaceMatch = line.match(/^\s*(export\s+)?interface\s+(\w+)/);
        if (interfaceMatch && interfaceMatch[2]) {
          symbols.push({ name: interfaceMatch[2], type: "interface", line: lineNum });
          continue;
        }
        const typeMatch = line.match(/^\s*(export\s+)?type\s+(\w+)/);
        if (typeMatch && typeMatch[2]) {
          symbols.push({ name: typeMatch[2], type: "type", line: lineNum });
          continue;
        }
        const funcMatch = line.match(/^\s*(export\s+)?(default\s+)?(async\s+)?function\s+(\w+)/);
        if (funcMatch && funcMatch[4]) {
          symbols.push({ name: funcMatch[4], type: "function", line: lineNum });
          continue;
        }
        const arrowFuncMatch = line.match(/^\s*(export\s+)?(const|let)\s+(\w+)\s*=\s*(async\s*)?\(.*?\)\s*=>/);
        if (arrowFuncMatch && arrowFuncMatch[3]) {
          symbols.push({ name: arrowFuncMatch[3], type: "function", line: lineNum });
          continue;
        }
        const methodMatch = line.match(/^\s*(async\s+)?(public|private|protected|static)?\s*(async)?\s*(\w+)\s*\(.*?\)\s*(:\s*[^{]+)?\s*\{/);
        if (methodMatch && methodMatch[4]) {
          const name = methodMatch[4];
          const reserved = /* @__PURE__ */ new Set(["if", "for", "while", "switch", "catch", "constructor", "get", "set"]);
          if (!reserved.has(name)) {
            symbols.push({ name, type: "method", line: lineNum });
          }
        }
      }
      return { ok: true, value: symbols };
    } catch (error) {
      throw new ToolExecutionError("get_code_outline", error);
    }
  }
};

// src/filesystem/list-directory.ts
import { readdir as readdir2 } from "fs/promises";
import { resolveSafePath as resolveSafePath3 } from "@vexalyn/context";
import { isAbsolute as isAbsolute2 } from "path";
import { z as z3 } from "zod";

// src/filesystem/helpers.ts
import { PathSecurityError, ToolExecutionError as ToolExecutionError2 } from "@vexalyn/shared";
import { basename, delimiter as pathDelimiter, isAbsolute, join, relative, resolve, sep } from "path";
import { readdir, stat } from "fs/promises";
import { resolveSafePath as resolveSafePath2 } from "@vexalyn/context";
import { exec } from "child_process";
import { promisify } from "util";
var execAsync = promisify(exec);
async function autoFixFile(filePath) {
  if (process.env.VITEST === "true") {
    return;
  }
  const projectRoot = process.cwd();
  try {
    await execAsync(`pnpm exec prettier --write "${filePath}"`, { cwd: projectRoot, timeout: 2e3 });
  } catch {
  }
  try {
    await execAsync(`pnpm exec eslint --fix "${filePath}"`, { cwd: projectRoot, timeout: 2e3 });
  } catch {
  }
}
async function safely(toolName, operation) {
  try {
    return { ok: true, value: await operation() };
  } catch (error) {
    if (error instanceof Error && error.name.endsWith("Error")) throw error;
    throw new ToolExecutionError2(toolName, error);
  }
}
var ignoredDirs = /* @__PURE__ */ new Set(["node_modules", ".git", "dist", ".turbo", "coverage"]);
async function findFilesByName(root, current, targetName, targetPath, results) {
  const entries = await readdir(current, { withFileTypes: true }).catch(() => []);
  for (const entry of entries) {
    if (ignoredDirs.has(entry.name)) continue;
    const entryPath = join(current, entry.name);
    if (entry.isDirectory()) {
      await findFilesByName(root, entryPath, targetName, targetPath, results);
      continue;
    }
    const relPath = relative(root, entryPath);
    const normalizedTarget = targetPath.replace(/\\/g, "/");
    const normalizedRel = relPath.replace(/\\/g, "/");
    if (entry.name.toLowerCase() === targetName.toLowerCase() || normalizedRel.endsWith(normalizedTarget)) {
      results.push(entryPath);
    }
  }
}
function extraAllowedRoots() {
  const raw = process.env.VEXALYN_EXTRA_ALLOWED_PATHS;
  if (!raw || !raw.trim()) return [];
  return raw.split(pathDelimiter).map((entry) => entry.trim()).filter(Boolean);
}
function getAllowedRoots(workspaceRoot) {
  return [resolve(workspaceRoot), ...extraAllowedRoots().map((root) => resolve(root))];
}
function ensureInsideAllowedRoots(workspaceRoot, inputPath) {
  const target = resolve(inputPath);
  const allowed = getAllowedRoots(workspaceRoot).some((root) => {
    const rel = relative(root, target);
    return rel === "" || rel !== ".." && !rel.startsWith(`..${sep}`) && !isAbsolute(rel);
  });
  if (!allowed) throw new PathSecurityError(inputPath);
  return target;
}
async function resolveSmartPath(workspaceRoot, inputPath) {
  if (isAbsolute(inputPath)) {
    const absoluteTarget = ensureInsideAllowedRoots(workspaceRoot, inputPath);
    try {
      await stat(absoluteTarget);
      return absoluteTarget;
    } catch (err) {
      if (err && typeof err === "object" && "code" in err && err.code !== "ENOENT") throw err;
      throw new Error(`File atau folder "${inputPath}" tidak ditemukan.`);
    }
  }
  const path = resolveSafePath2(workspaceRoot, inputPath);
  try {
    await stat(path);
    return path;
  } catch (err) {
    if (err && typeof err === "object" && "code" in err && err.code !== "ENOENT") {
      throw err;
    }
  }
  const targetName = basename(inputPath);
  const matches = [];
  await findFilesByName(workspaceRoot, workspaceRoot, targetName, inputPath, matches);
  if (matches.length === 1) {
    return matches[0];
  } else if (matches.length > 1) {
    const bestMatch = matches.find((m) => {
      const normalizedM = relative(workspaceRoot, m).replace(/\\/g, "/");
      const normalizedInput = inputPath.replace(/\\/g, "/");
      return normalizedM.endsWith(normalizedInput);
    });
    if (bestMatch) {
      return bestMatch;
    }
    throw new Error(`File "${inputPath}" tidak ditemukan. Ditemukan beberapa file serupa di workspace:
${matches.map((m) => `- ${relative(workspaceRoot, m)}`).join("\n")}
Silakan gunakan path yang lebih lengkap.`);
  }
  throw new Error(`File "${inputPath}" tidak ditemukan di workspace.`);
}
var PLACEHOLDER_REGEX = /(\/\/|#|\/\*)\s*\.\.\.\s*|(\/\/|#)\s*(rest of|existing|code remains|same)/i;
function hasPlaceholder(text) {
  return PLACEHOLDER_REGEX.test(text);
}

// src/filesystem/list-directory.ts
var inputSchema3 = z3.object({ path: z3.string() });
var listDirectoryTool = {
  name: "list_directory",
  description: "List entries in a workspace directory. Use only when the user explicitly asks to see directory structure or files in a folder. Do not use this for content or filename searches; use search_files instead.",
  inputSchema: inputSchema3,
  execute: (input, context) => safely("list_directory", async () => {
    const requested = input.path ?? ".";
    const directory = isAbsolute2(requested) ? ensureInsideAllowedRoots(context.workspaceRoot, requested) : resolveSafePath3(context.workspaceRoot, requested);
    return readdir2(directory, { withFileTypes: true }).then((entries) => entries.map((entry) => `${entry.isDirectory() ? "dir" : "file"}	${entry.name}`));
  })
};

// src/filesystem/read-file.ts
import { stat as stat2, readFile as readFile2 } from "fs/promises";
import { FileTooLargeError, ToolExecutionError as ToolExecutionError3 } from "@vexalyn/shared";
import { relative as relative2 } from "path";
import { z as z4 } from "zod";
var inputSchema4 = z4.object({ path: z4.string() });
var readFileTool = {
  name: "read_file",
  description: "Read a UTF-8 text file, limited to 50KB and 2000 lines.",
  inputSchema: inputSchema4,
  async execute(input, context) {
    try {
      const path = await resolveSmartPath(context.workspaceRoot, input.path);
      const info = await stat2(path);
      const displayPath = relative2(context.workspaceRoot, path);
      if (info.size > 50 * 1024) throw new FileTooLargeError(displayPath, "50KB");
      const content = await readFile2(path, "utf8");
      if (content.split(/\r?\n/).length > 2e3) throw new FileTooLargeError(displayPath, "2000 lines");
      return { ok: true, value: content };
    } catch (error) {
      if (error instanceof Error && error.name.endsWith("Error")) throw error;
      throw new ToolExecutionError3("read_file", error);
    }
  }
};

// src/filesystem/write-file.ts
import { mkdir, writeFile } from "fs/promises";
import { dirname } from "path";
import { resolveSafePath as resolveSafePath4 } from "@vexalyn/context";
import { PermissionDeniedError, ToolExecutionError as ToolExecutionError4 } from "@vexalyn/shared";
import { z as z5 } from "zod";
var inputSchema5 = z5.object({ path: z5.string(), content: z5.string() });
var writeFileTool = {
  name: "write_file",
  description: "Write UTF-8 content to a workspace file.",
  inputSchema: inputSchema5,
  async execute(input, context) {
    try {
      if (hasPlaceholder(input.content)) {
        throw new Error("Pengecekan Keamanan: Konten yang dikirim terdeteksi mengandung placeholder malas seperti '// ...' atau 'rest of code'. Harap tulis kode secara lengkap tanpa menghilangkan bagian kode lain.");
      }
      const path = resolveSafePath4(context.workspaceRoot, input.path);
      if (context.approve && !await context.approve("write", input.path)) throw new PermissionDeniedError("write", input.path);
      await mkdir(dirname(path), { recursive: true });
      await writeFile(path, input.content, "utf8");
      await autoFixFile(path);
      return { ok: true, value: path };
    } catch (error) {
      if (error instanceof Error && error.name.endsWith("Error")) throw error;
      throw new ToolExecutionError4("write_file", error);
    }
  }
};

// src/filesystem/edit-file.ts
import { readFile as readFile3, writeFile as writeFile2 } from "fs/promises";
import { relative as relative3 } from "path";
import { FileEditConflictError, PermissionDeniedError as PermissionDeniedError2, ToolExecutionError as ToolExecutionError5 } from "@vexalyn/shared";
import { z as z6 } from "zod";
var inputSchema6 = z6.object({
  path: z6.string().describe("The path of the file to edit."),
  oldText: z6.string().describe("The exact text to be replaced."),
  newText: z6.string().describe("The replacement text."),
  allowMultiple: z6.boolean().describe("Set to true if oldText appears more than once and you want to replace all occurrences. Otherwise set to false.")
});
function findFuzzyMatches(content, oldText) {
  const contentLines = content.split("\n");
  const oldLines = oldText.split("\n").map((l) => l.trim()).filter(Boolean);
  const results = [];
  if (oldLines.length === 0) return results;
  for (let i = 0; i <= contentLines.length - oldLines.length; i++) {
    let match = true;
    for (let j = 0; j < oldLines.length; j++) {
      const contentLineClean = contentLines[i + j].trim();
      const oldLineClean = oldLines[j];
      if (contentLineClean !== oldLineClean) {
        match = false;
        break;
      }
    }
    if (match) {
      const beforeLines = contentLines.slice(0, i);
      const matchLines = contentLines.slice(i, i + oldLines.length);
      const startIdx = beforeLines.join("\n").length + (beforeLines.length > 0 ? 1 : 0);
      const endIdx = startIdx + matchLines.join("\n").length;
      results.push({ startIdx, endIdx });
      i += oldLines.length - 1;
    }
  }
  return results;
}
var editFileTool = {
  name: "edit_file",
  description: "Replace occurrences of oldText in a workspace file.",
  inputSchema: inputSchema6,
  async execute(input, context) {
    try {
      if (hasPlaceholder(input.newText)) {
        throw new Error("Pengecekan Keamanan: Konten yang dikirim terdeteksi mengandung placeholder malas seperti '// ...' atau 'rest of code'. Harap tulis kode secara lengkap tanpa menghilangkan bagian kode lain.");
      }
      const path = await resolveSmartPath(context.workspaceRoot, input.path);
      const displayPath = relative3(context.workspaceRoot, path);
      if (context.approve && !await context.approve("edit", displayPath)) throw new PermissionDeniedError2("edit", displayPath);
      const content = await readFile3(path, "utf8");
      const matches = input.oldText === "" ? 0 : content.split(input.oldText).length - 1;
      let newContent = content;
      if (matches > 0) {
        if (matches > 1 && !input.allowMultiple) throw new FileEditConflictError(displayPath, matches);
        newContent = input.allowMultiple ? content.split(input.oldText).join(input.newText) : content.replace(input.oldText, input.newText);
      } else {
        const fuzzyMatches = findFuzzyMatches(content, input.oldText);
        if (fuzzyMatches.length === 0) throw new FileEditConflictError(displayPath, 0);
        if (fuzzyMatches.length > 1 && !input.allowMultiple) throw new FileEditConflictError(displayPath, fuzzyMatches.length);
        for (let idx = fuzzyMatches.length - 1; idx >= 0; idx--) {
          const m = fuzzyMatches[idx];
          const before = newContent.substring(0, m.startIdx);
          const after = newContent.substring(m.endIdx);
          newContent = before + input.newText + after;
        }
      }
      await writeFile2(path, newContent, "utf8");
      await autoFixFile(path);
      return { ok: true, value: path };
    } catch (error) {
      if (error instanceof Error && error.name.endsWith("Error")) throw error;
      throw new ToolExecutionError5("edit_file", error);
    }
  }
};

// src/filesystem/apply-patch.ts
import { mkdir as mkdir2, readFile as readFile4, rm, writeFile as writeFile3 } from "fs/promises";
import { dirname as dirname2 } from "path";
import { resolveSafePath as resolveSafePath5 } from "@vexalyn/context";
import { PermissionDeniedError as PermissionDeniedError3, ToolExecutionError as ToolExecutionError6 } from "@vexalyn/shared";
import { z as z7 } from "zod";
var inputSchema7 = z7.object({
  patchText: z7.string().min(1).describe(
    "Patch in this exact format:\n*** Begin Patch\n*** Add File: path/new.ts\n+full file content lines\n*** Update File: src/existing.ts\n context line\n-removed line\n+added line\n*** Delete File: src/old.ts\n*** End Patch"
  )
});
var MARKER = /^\*\*\* (Add File|Update File|Delete File|Move to): (.+?)\s*$/;
function parsePatch(patchText) {
  const files = [];
  let current = null;
  let inPatchBody = false;
  for (const rawLine of patchText.split("\n")) {
    const line = rawLine.replace(/\r$/, "");
    const marker = line.match(MARKER);
    if (marker) {
      const [, action, target] = marker;
      const cleanTarget = target.trim().replace(/\\/g, "/");
      if (action === "Add File") {
        current = { kind: "add", path: cleanTarget, contentLines: [], ops: [] };
        files.push(current);
        inPatchBody = true;
      } else if (action === "Update File") {
        current = { kind: "update", path: cleanTarget, ops: [] };
        files.push(current);
        inPatchBody = true;
      } else if (action === "Delete File") {
        current = { kind: "delete", path: cleanTarget, ops: [] };
        files.push(current);
        inPatchBody = true;
      } else if (action === "Move to") {
        if (current && current.kind === "update") current.moveTo = cleanTarget;
      }
      continue;
    }
    if (/^\s*\*{3}\s*End Patch\s*$/.test(line)) break;
    if (!inPatchBody || !current) continue;
    if (current.kind === "add") {
      if (line.startsWith("+")) current.contentLines.push(line.slice(1));
      continue;
    }
    if (current.kind !== "update") continue;
    if (/^(--- |\+\+\+ |@@ )/.test(line)) continue;
    const type = line[0];
    if (type === "+" || type === "-") {
      current.ops.push({ type, line: line.slice(1) });
    } else if (line.startsWith(" ") || line === "") {
      current.ops.push({ type: " ", line: line.slice(1) });
    }
  }
  return files.filter((f) => f.kind !== "update" || f.ops.length > 0 || Boolean(f.moveTo));
}
function applyUpdateOps(originalLines, ops, filePath) {
  const result = [];
  let pointer = 0;
  for (const op of ops) {
    if (op.type === "+") {
      result.push(op.line);
      continue;
    }
    if (pointer >= originalLines.length) {
      throw new Error(`Patch tidak cocok untuk "${filePath}": baris "${op.line}" berada di luar isi file.`);
    }
    const expected = originalLines[pointer];
    if (op.type === " " && op.line === "" && expected === "") {
      result.push(expected);
      pointer += 1;
      continue;
    }
    if (expected !== op.line) {
      throw new Error(`Patch tidak cocok untuk "${filePath}" pada baris ${pointer + 1}: diharapkan "${op.line}", ditemukan "${expected}". Baca ulang file dengan read_file lalu buat patch yang persis cocok.`);
    }
    if (op.type === " ") result.push(expected);
    pointer += 1;
  }
  result.push(...originalLines.slice(pointer));
  return result;
}
var applyPatchTool = {
  name: "apply_patch",
  description: "Apply a multi-file patch atomically: add new files, update existing files via exact context/-/+ lines, move, or delete files. Prefer this over multiple write_file/edit_file calls when changing several files at once. Paths are workspace-relative.",
  inputSchema: inputSchema7,
  async execute(input, context) {
    try {
      const text = input.patchText.replace(/^```[a-z]*\r?\n?/i, "").replace(/\r?\n?```\s*$/i, "");
      const files = parsePatch(text);
      if (files.length === 0) {
        return { ok: false, error: 'Patch kosong atau format salah. Gunakan blok "*** Begin Patch" ... "*** End Patch" dengan penanda *** Add File / Update File / Delete File.' };
      }
      const plannedWrites = [];
      const plannedDeletes = [];
      for (const file of files) {
        if (file.path.startsWith("/") || /^[a-zA-Z]:[\\/]/.test(file.path) || file.moveTo && (file.moveTo.startsWith("/") || /^[a-zA-Z]:[\\/]/.test(file.moveTo))) {
          return { ok: false, error: `Path harus relatif terhadap workspace: "${file.path}"` };
        }
        if (file.kind === "add") {
          const contentText = (file.contentLines ?? []).join("\n");
          if (hasPlaceholder(contentText)) {
            return { ok: false, error: `Konten Add File untuk "${file.path}" mengandung placeholder malas seperti "// ..." atau "rest of code". Tulis kode lengkap.` };
          }
          const absPath = resolveSafePath5(context.workspaceRoot, file.path);
          if (context.approve && !await context.approve("write", file.path)) throw new PermissionDeniedError3("write", file.path);
          plannedWrites.push({ absPath, label: file.path, content: contentText, verb: `ditambahkan (${(file.contentLines ?? []).length} baris)` });
          continue;
        }
        if (file.kind === "delete") {
          const absPath = resolveSafePath5(context.workspaceRoot, file.path);
          if (context.approve && !await context.approve("delete", file.path)) throw new PermissionDeniedError3("delete", file.path);
          plannedDeletes.push({ absPath, label: file.path });
          continue;
        }
        const absOriginal = resolveSafePath5(context.workspaceRoot, file.path);
        let original;
        try {
          original = await readFile4(absOriginal, "utf8");
        } catch {
          return { ok: false, error: `Gagal membaca "${file.path}" untuk update. Pastikan file ada \u2014 baca dulu dengan read_file sebelum membuat patch.` };
        }
        let updatedLines;
        try {
          updatedLines = applyUpdateOps(original.split("\n"), file.ops, file.path);
        } catch (mismatchError) {
          return { ok: false, error: mismatchError instanceof Error ? mismatchError.message : String(mismatchError) };
        }
        const addedCount = file.ops.filter((op) => op.type === "+").length;
        const removedCount = file.ops.filter((op) => op.type === "-").length;
        const finalAbs = file.moveTo ? resolveSafePath5(context.workspaceRoot, file.moveTo) : absOriginal;
        if (context.approve && !await context.approve("edit", file.moveTo ?? file.path)) throw new PermissionDeniedError3("edit", file.moveTo ?? file.path);
        if (file.moveTo && finalAbs !== absOriginal) plannedDeletes.push({ absPath: absOriginal, label: file.path });
        plannedWrites.push({ absPath: finalAbs, label: file.moveTo ?? file.path, content: updatedLines.join("\n"), verb: `diupdate (+${addedCount} -${removedCount})${file.moveTo ? ", dipindah dari " + file.path : ""}` });
      }
      for (const write of plannedWrites) {
        await mkdir2(dirname2(write.absPath), { recursive: true });
        await writeFile3(write.absPath, write.content, "utf8");
        await autoFixFile(write.absPath);
      }
      for (const del of plannedDeletes) {
        await rm(del.absPath, { force: true });
      }
      const summaryParts = [
        ...plannedWrites.map((w) => `- ${w.label} ${w.verb}`),
        ...plannedDeletes.map((d) => `- ${d.label} dihapus`)
      ];
      return { ok: true, value: `Patch diterapkan:
${summaryParts.join("\n")}` };
    } catch (error) {
      if (error instanceof Error && error.name.endsWith("Error")) throw error;
      throw new ToolExecutionError6("apply_patch", error);
    }
  }
};

// src/interactive/question.ts
import { ToolExecutionError as ToolExecutionError7 } from "@vexalyn/shared";
import { z as z8 } from "zod";
var inputSchema8 = z8.object({
  question: z8.string().min(1).describe("The question to ask the user. Be specific and self-contained."),
  options: z8.array(z8.string()).min(2).max(6).optional().describe("Up to 6 short answer choices the user can pick from."),
  multiple: z8.boolean().optional().describe("Allow selecting more than one option.")
});
var questionTool = {
  name: "question",
  description: "Ask the user a clarifying question when requirements are ambiguous before doing destructive or large work. Optionally provide 2-6 answer choices; the user can also type a custom answer.",
  inputSchema: inputSchema8,
  async execute(input, context) {
    try {
      if (!context.ask) {
        return { ok: false, error: "Tool question tidak tersedia di sesi ini (tidak ada kanal interaksi user)." };
      }
      const { answers } = await context.ask({ question: input.question, options: input.options, multiple: input.multiple });
      if (!answers || answers.length === 0) {
        return { ok: true, value: "User tidak memberikan jawaban. Lanjutkan dengan asumsi teraman dan sebutkan asumsi itu ke user." };
      }
      return { ok: true, value: `Jawaban user: ${answers.join("; ")}` };
    } catch (error) {
      if (error instanceof Error && error.name.endsWith("Error")) throw error;
      throw new ToolExecutionError7("question", error);
    }
  }
};

// src/lsp/lsp.ts
import { readFile as readFile5 } from "fs/promises";
import { relative as relative4 } from "path";
import { resolveSafePath as resolveSafePath6 } from "@vexalyn/context";
import { ToolExecutionError as ToolExecutionError8 } from "@vexalyn/shared";
import * as ts from "typescript";
import { z as z9 } from "zod";
var inputSchema9 = z9.object({
  action: z9.enum(["diagnostics", "hover", "symbols", "references"]).describe("Diagnostics/hover/symbols/references for TypeScript & JavaScript files."),
  path: z9.string().describe("Workspace-relative file path (.ts/.tsx/.js/.jsx/.mts/.cts)."),
  line: z9.number().int().min(1).optional().describe("1-based line for hover/references."),
  column: z9.number().int().min(1).optional().describe("1-based column for hover/references.")
});
var serviceCache = /* @__PURE__ */ new Map();
function loadCompilerOptions(workspaceRoot) {
  const configPath = ts.findConfigFile(workspaceRoot, ts.sys.fileExists, "tsconfig.json");
  if (!configPath) {
    return {
      target: ts.ScriptTarget.ES2022,
      module: ts.ModuleKind.ESNext,
      moduleResolution: ts.ModuleResolutionKind.Bundler,
      strict: true,
      skipLibCheck: true,
      esModuleInterop: true,
      resolveJsonModule: true,
      allowJs: true,
      noEmit: true
    };
  }
  const configFile = ts.readConfigFile(configPath, ts.sys.readFile);
  const parsed = ts.parseJsonConfigFileContent(configFile.config ?? {}, ts.sys, workspaceRoot);
  return { ...parsed.options, noEmit: true, skipLibCheck: true };
}
function getService(workspaceRoot, filePath) {
  let entry = serviceCache.get(workspaceRoot);
  if (!entry) {
    const compilerOptions = loadCompilerOptions(workspaceRoot);
    entry = {
      service: null,
      files: /* @__PURE__ */ new Set(),
      version: 0
    };
    const host = {
      getScriptFileNames: () => [...entry.files],
      getScriptVersion: () => String(entry.version),
      getScriptSnapshot: (name) => {
        const text = ts.sys.readFile(name);
        return text === void 0 ? void 0 : ts.ScriptSnapshot.fromString(text);
      },
      getCurrentDirectory: () => workspaceRoot,
      getCompilationSettings: () => compilerOptions,
      getDefaultLibFileName: (options) => ts.getDefaultLibFilePath(options),
      fileExists: ts.sys.fileExists,
      readFile: ts.sys.readFile,
      readDirectory: ts.sys.readDirectory,
      directoryExists: ts.sys.directoryExists,
      getDirectories: ts.sys.getDirectories
    };
    entry.service = ts.createLanguageService(host);
    serviceCache.set(workspaceRoot, entry);
  }
  if (!entry.files.has(filePath)) {
    if (entry.files.size > 400) {
      const first = [...entry.files][0];
      entry.files.delete(first);
    }
    entry.files.add(filePath);
    entry.version += 1;
  }
  return entry;
}
function toPosition(content, line, column) {
  if (!line || !column) return 0;
  const lines = content.split("\n");
  let position = 0;
  for (let i = 0; i < Math.min(line - 1, lines.length); i++) position += lines[i].length + 1;
  return position + Math.max(0, column - 1);
}
var lspTool = {
  name: "lsp",
  description: "TypeScript/JavaScript language intelligence via the bundled TypeScript compiler: check type errors in a file, hover a symbol for its type, list document symbols, or find references.",
  inputSchema: inputSchema9,
  async execute(input, context) {
    try {
      const absPath = resolveSafePath6(context.workspaceRoot, input.path);
      if (!/\.(ts|tsx|mts|cts|js|jsx|mjs|cjs)$/i.test(absPath)) {
        return { ok: false, error: `lsp hanya mendukung file TS/JS. Gunakan grep/glob/read_file untuk "${input.path}".` };
      }
      let content;
      try {
        content = await readFile5(absPath, "utf8");
      } catch {
        return { ok: false, error: `File tidak ditemukan atau tidak bisa dibaca: ${input.path}` };
      }
      const { service } = getService(context.workspaceRoot, absPath);
      if (input.action === "diagnostics") {
        const syntactic = service.getSyntacticDiagnostics(absPath);
        const semantic = service.getSemanticDiagnostics(absPath);
        const all = [...syntactic, ...semantic];
        if (all.length === 0) return { ok: true, value: `${input.path}: tidak ada error tipe/sintaks.` };
        const rows2 = all.slice(0, 60).map((diag) => {
          const start = diag.file?.getLineAndCharacterOfPosition(diag.start ?? 0);
          const severity = diag.category === ts.DiagnosticCategory.Error ? "error" : "warning";
          return `${severity} ${input.path}:${(start?.line ?? 0) + 1}:${(start?.character ?? 0) + 1} \u2014 ${ts.flattenDiagnosticMessageText(diag.messageText, "\n")}`;
        });
        const suffix = all.length > 60 ? `
... dan ${all.length - 60} diagnostik lainnya` : "";
        return { ok: true, value: `${all.length} diagnostik:
${rows2.join("\n")}${suffix}` };
      }
      if (input.action === "symbols") {
        const nav = service.getNavigationTree(absPath);
        const rows2 = [];
        const walk = (node, depth) => {
          if (rows2.length < 200) rows2.push(`${"  ".repeat(depth)}${node.text} (${node.kind.toLowerCase()})`);
          for (const child of node.childItems ?? []) walk(child, depth + 1);
        };
        walk(nav, 0);
        if (rows2.length === 0) return { ok: true, value: `${input.path}: tidak ada simbol.` };
        return { ok: true, value: `Simbol di ${input.path}:
${rows2.join("\n")}` };
      }
      if (!input.line || !input.column) {
        return { ok: false, error: `Aksi "${input.action}" membutuhkan parameter line dan column (1-based). Gunakan read_file dulu untuk menemukan posisi.` };
      }
      const position = toPosition(content, input.line, input.column);
      if (input.action === "hover") {
        const info = service.getQuickInfoAtPosition(absPath, position);
        if (!info || !info.displayParts) return { ok: true, value: `Tidak ada info tipe di ${input.path}:${input.line}:${input.column}.` };
        return { ok: true, value: `${info.displayParts.map((part) => part.text).join("")}` };
      }
      const referenced = service.findReferences(absPath, position);
      if (!referenced || referenced.length === 0) return { ok: true, value: `Tidak ada referensi ke simbol di ${input.path}:${input.line}:${input.column}.` };
      const program = service.getProgram();
      const rows = [];
      outer: for (const group of referenced) {
        for (const ref of group.references) {
          const sourceFile = program?.getSourceFile(ref.fileName);
          const pos = sourceFile?.getLineAndCharacterOfPosition(ref.textSpan.start);
          const displayPath = ref.fileName === absPath ? input.path : relative4(context.workspaceRoot, ref.fileName).split("\\").join("/");
          rows.push(`${displayPath}:${(pos?.line ?? 0) + 1}:${(pos?.character ?? 0) + 1}`);
          if (rows.length >= 80) break outer;
        }
      }
      return { ok: true, value: `Referensi:
${rows.join("\n")}` };
    } catch (error) {
      if (error instanceof Error && error.name.endsWith("Error")) throw error;
      throw new ToolExecutionError8("lsp", error);
    }
  }
};

// src/skill/skill.ts
import { homedir } from "os";
import { readFile as readFile6 } from "fs/promises";
import { join as join2 } from "path";
import { ToolExecutionError as ToolExecutionError9 } from "@vexalyn/shared";
import { z as z10 } from "zod";
var inputSchema10 = z10.object({
  name: z10.string().min(1).max(64).regex(/^[A-Za-z0-9_-]+$/, "Nama skill hanya boleh huruf, angka, strip, dan underscore.").describe("Name of the skill folder containing SKILL.md.")
});
function skillDirectories(workspaceRoot) {
  return [join2(workspaceRoot, ".vexalyn", "skills"), join2(homedir(), ".vexalyn", "skills")];
}
async function findSkillFile(workspaceRoot, name) {
  for (const dir of skillDirectories(workspaceRoot)) {
    const candidate = join2(dir, name, "SKILL.md");
    try {
      const stat9 = await readFile6(candidate, "utf8");
      if (stat9 !== void 0) return candidate;
    } catch {
      continue;
    }
  }
  return null;
}
async function listSkills(workspaceRoot) {
  const names = /* @__PURE__ */ new Set();
  const { readdir: readdir6 } = await import("fs/promises");
  for (const dir of skillDirectories(workspaceRoot)) {
    try {
      const entries = await readdir6(dir, { withFileTypes: true });
      for (const entry of entries) {
        if (!entry.isDirectory()) continue;
        const hasSkill = await findSkillFile(workspaceRoot, entry.name);
        if (hasSkill && !names.has(entry.name)) names.add(entry.name);
      }
    } catch {
      continue;
    }
  }
  return [...names].sort();
}
var skillTool = {
  name: "skill",
  description: "Load specialized instructions from an installed skill (a folder with SKILL.md under .vexalyn/skills). Use when a task matches an available skill.",
  inputSchema: inputSchema10,
  async execute(input, context) {
    try {
      if (!/^[A-Za-z0-9_-]+$/.test(input.name)) {
        return { ok: false, error: "Nama skill tidak valid. Gunakan hanya huruf, angka, strip, dan underscore." };
      }
      const skillPath = await findSkillFile(context.workspaceRoot, input.name);
      if (!skillPath) {
        const available = await listSkills(context.workspaceRoot);
        return { ok: false, error: `Skill "${input.name}" tidak ditemukan.${available.length > 0 ? ` Skill tersedia: ${available.join(", ")}.` : " Belum ada skill terpasang di .vexalyn/skills."}` };
      }
      const content = await readFile6(skillPath, "utf8");
      return { ok: true, value: `Skill "${input.name}" dimuat dari ${skillPath}.

${content.trim()}` };
    } catch (error) {
      if (error instanceof Error && error.name.endsWith("Error")) throw error;
      throw new ToolExecutionError9("skill", error);
    }
  }
};

// src/git/diff-engine.ts
var MAX_DP_CELLS = 4e6;
var MAX_CONTEXT = 6;
function splitLines(text) {
  if (text.length === 0) return [];
  const lines = text.split("\n");
  if (lines.length > 1 && lines[lines.length - 1] === "") lines.pop();
  return lines;
}
function trimCommonEdges(a, b) {
  let prefix = 0;
  while (prefix < a.length && prefix < b.length && a[prefix] === b[prefix]) prefix += 1;
  let suffix = 0;
  while (suffix < a.length - prefix && suffix < b.length - prefix && a[a.length - 1 - suffix] === b[b.length - 1 - suffix]) {
    suffix += 1;
  }
  return { prefix, suffix };
}
function lcsOps(a, b, offset) {
  const cols = b.length + 1;
  const table = new Uint32Array((a.length + 1) * cols);
  for (let i2 = a.length - 1; i2 >= 0; i2--) {
    for (let j2 = b.length - 1; j2 >= 0; j2--) {
      table[i2 * cols + j2] = a[i2] === b[j2] ? table[(i2 + 1) * cols + j2 + 1] + 1 : Math.max(table[(i2 + 1) * cols + j2], table[i2 * cols + j2 + 1]);
    }
  }
  const ops = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      ops.push(["context", offset + i, offset + j]);
      i += 1;
      j += 1;
    } else if (table[(i + 1) * cols + j] >= table[i * cols + j + 1]) {
      ops.push(["del", offset + i, offset + j]);
      i += 1;
    } else {
      ops.push(["add", offset + i, offset + j]);
      j += 1;
    }
  }
  while (i < a.length) {
    ops.push(["del", offset + i, offset + j]);
    i += 1;
  }
  while (j < b.length) {
    ops.push(["add", offset + i, offset + j]);
    j += 1;
  }
  return ops;
}
function buildOps(originalLines, modifiedLines) {
  const { prefix, suffix } = trimCommonEdges(originalLines, modifiedLines);
  const ops = [];
  for (let i = 0; i < prefix; i++) {
    ops.push({ type: "context", text: originalLines[i], oldIndex: i, newIndex: i });
  }
  const midOld = originalLines.slice(prefix, originalLines.length - suffix);
  const midNew = modifiedLines.slice(prefix, modifiedLines.length - suffix);
  if (midOld.length * midNew.length <= MAX_DP_CELLS) {
    for (const [type, oldIndex, newIndex] of lcsOps(midOld, midNew, prefix)) {
      const text = type === "add" ? modifiedLines[newIndex] : originalLines[oldIndex];
      ops.push({ type, text, oldIndex, newIndex });
    }
  } else {
    for (let i = 0; i < midOld.length; i++) {
      ops.push({ type: "del", text: midOld[i], oldIndex: prefix + i, newIndex: prefix });
    }
    for (let j = 0; j < midNew.length; j++) {
      ops.push({ type: "add", text: midNew[j], oldIndex: prefix, newIndex: prefix + j });
    }
  }
  for (let s = 0; s < suffix; s++) {
    const oldIndex = originalLines.length - suffix + s;
    const newIndex = modifiedLines.length - suffix + s;
    ops.push({ type: "context", text: originalLines[oldIndex], oldIndex, newIndex });
  }
  return ops;
}
function groupHunks(ops, context) {
  const changeIndexes = [];
  for (let i = 0; i < ops.length; i++) {
    if (ops[i].type !== "context") changeIndexes.push(i);
  }
  if (changeIndexes.length === 0) return [];
  const windows = [];
  for (const changeIndex of changeIndexes) {
    const start = Math.max(0, changeIndex - context);
    const end = Math.min(ops.length, changeIndex + context + 1);
    const last = windows[windows.length - 1];
    if (last && start <= last[1]) {
      last[1] = Math.max(last[1], end);
    } else {
      windows.push([start, end]);
    }
  }
  let oldBefore = 0;
  let newBefore = 0;
  let cursor = 0;
  const hunks = [];
  for (const [start, end] of windows) {
    while (cursor < start) {
      const op = ops[cursor];
      if (op.type !== "add") oldBefore += 1;
      if (op.type !== "del") newBefore += 1;
      cursor += 1;
    }
    const lines = [];
    for (let k = start; k < end; k++) {
      lines.push({ type: ops[k].type, text: ops[k].text });
    }
    hunks.push({
      oldStart: oldBefore + 1,
      oldLines: lines.filter((line) => line.type !== "add").length,
      newStart: newBefore + 1,
      newLines: lines.filter((line) => line.type !== "del").length,
      lines
    });
    for (let k = start; k < end; k++) {
      const op = ops[k];
      if (op.type !== "add") oldBefore += 1;
      if (op.type !== "del") newBefore += 1;
    }
  }
  return hunks;
}
function computeUnifiedDiff(original, modified, options) {
  const context = options?.context ?? 3;
  const hunks = groupHunks(buildOps(splitLines(original), splitLines(modified)), Math.max(0, Math.min(MAX_CONTEXT, context)));
  return hunks.filter((hunk) => hunk.lines.some((line) => line.type !== "context"));
}
function diffStats(hunks) {
  let added = 0;
  let removed = 0;
  for (const hunk of hunks) {
    for (const line of hunk.lines) {
      if (line.type === "add") added += 1;
      else if (line.type === "del") removed += 1;
    }
  }
  return { added, removed };
}
var ANSI_RESET = "\x1B[0m";
var ANSI_GREEN = "\x1B[38;2;74;222;128m";
var ANSI_RED = "\x1B[91m";
var ANSI_CYAN_DIM = "\x1B[2;96m";
var ANSI_BOLD_WHITE = "\x1B[97m";
function formatDiffText(hunks, options = {}) {
  const { maxLines = 40, color = true } = options;
  const allRows = [];
  for (const hunk of hunks) {
    const header = `@@ -${hunk.oldStart},${hunk.oldLines} +${hunk.newStart},${hunk.newLines} @@`;
    allRows.push(color ? `${ANSI_CYAN_DIM}${header}${ANSI_RESET}` : header);
    for (const line of hunk.lines) {
      if (line.type === "add") {
        allRows.push(color ? `${ANSI_GREEN}+${line.text}${ANSI_RESET}` : `+${line.text}`);
      } else if (line.type === "del") {
        allRows.push(color ? `${ANSI_RED}-${line.text}${ANSI_RESET}` : `-${line.text}`);
      } else {
        allRows.push(`  ${line.text}`);
      }
    }
  }
  const total = diffStats(hunks);
  const summary = `${total.added} baris ditambah, ${total.removed} dihapus`;
  const summaryOut = color ? `${ANSI_BOLD_WHITE}${summary}${ANSI_RESET}` : summary;
  if (allRows.length === 0) return summaryOut;
  const visible = allRows.slice(0, Math.max(0, maxLines));
  const hidden = allRows.length - visible.length;
  let out = `${summaryOut}
${visible.join("\n")}`;
  if (hidden > 0) {
    out += `
${color ? ANSI_CYAN_DIM : ""}\u2026 ${hidden} baris diff lagi${color ? ANSI_RESET : ""}`;
  }
  return out;
}

// src/filesystem/delete-file.ts
import { unlink, rm as rm2, stat as stat3 } from "fs/promises";
import { relative as relative5 } from "path";
import { PermissionDeniedError as PermissionDeniedError4, ToolExecutionError as ToolExecutionError10 } from "@vexalyn/shared";
import { z as z11 } from "zod";
var inputSchema11 = z11.object({ path: z11.string() });
var deleteFileTool = {
  name: "delete_file",
  description: "Delete a workspace file or directory (recursive) after permission approval.",
  inputSchema: inputSchema11,
  async execute(input, context) {
    try {
      const path = await resolveSmartPath(context.workspaceRoot, input.path);
      const displayPath = relative5(context.workspaceRoot, path);
      if (context.approve && !await context.approve("delete", displayPath)) throw new PermissionDeniedError4("delete", displayPath);
      const info = await stat3(path);
      if (info.isDirectory()) {
        await rm2(path, { recursive: true, force: true });
      } else {
        await unlink(path);
      }
      return { ok: true, value: path };
    } catch (error) {
      if (error instanceof Error && error.name.endsWith("Error")) throw error;
      throw new ToolExecutionError10("delete_file", error);
    }
  }
};

// src/filesystem/create-directory.ts
import { mkdir as mkdir3 } from "fs/promises";
import { relative as relative6 } from "path";
import { resolveSafePath as resolveSafePath7 } from "@vexalyn/context";
import { PermissionDeniedError as PermissionDeniedError5, ToolExecutionError as ToolExecutionError11 } from "@vexalyn/shared";
import { z as z12 } from "zod";
var inputSchema12 = z12.object({ path: z12.string().min(1) });
var createDirectoryTool = {
  name: "create_directory",
  description: "Create a workspace directory and its missing parent directories after permission approval.",
  inputSchema: inputSchema12,
  async execute(input, context) {
    try {
      const directory = resolveSafePath7(context.workspaceRoot, input.path);
      const displayPath = relative6(context.workspaceRoot, directory) || ".";
      if (context.approve && !await context.approve("write", displayPath)) {
        throw new PermissionDeniedError5("write", displayPath);
      }
      await mkdir3(directory, { recursive: true });
      return { ok: true, value: directory };
    } catch (error) {
      if (error instanceof Error && error.name.endsWith("Error")) throw error;
      throw new ToolExecutionError11("create_directory", error);
    }
  }
};

// src/filesystem/delete-directory.ts
import { rm as rm3, stat as stat4 } from "fs/promises";
import { relative as relative7, resolve as resolve2 } from "path";
import { PermissionDeniedError as PermissionDeniedError6, ToolExecutionError as ToolExecutionError12 } from "@vexalyn/shared";
import { z as z13 } from "zod";
var inputSchema13 = z13.object({ path: z13.string().min(1) });
var deleteDirectoryTool = {
  name: "delete_directory",
  description: "Delete a workspace directory recursively after permission approval.",
  inputSchema: inputSchema13,
  async execute(input, context) {
    try {
      const directory = await resolveSmartPath(context.workspaceRoot, input.path);
      if (getAllowedRoots(context.workspaceRoot).includes(resolve2(directory))) {
        throw new Error("Tidak boleh menghapus workspace root atau folder root yang diizinkan.");
      }
      const info = await stat4(directory);
      if (!info.isDirectory()) {
        throw new Error(`Path bukan folder: ${input.path}`);
      }
      const displayPath = relative7(resolve2(context.workspaceRoot), directory);
      if (context.approve && !await context.approve("delete", displayPath)) {
        throw new PermissionDeniedError6("delete", displayPath);
      }
      await rm3(directory, { recursive: true, force: true });
      return { ok: true, value: directory };
    } catch (error) {
      if (error instanceof Error && error.name.endsWith("Error")) throw error;
      throw new ToolExecutionError12("delete_directory", error);
    }
  }
};

// src/filesystem/get-project-structure.ts
import { createIgnoreMatcher, loadIgnoreRules, resolveSafePath as resolveSafePath8, scanProjectStructure } from "@vexalyn/context";
import { ToolExecutionError as ToolExecutionError13 } from "@vexalyn/shared";
import { z as z14 } from "zod";
var inputSchema14 = z14.object({
  path: z14.string().default("."),
  maxDepth: z14.number().int().min(0).max(10).default(3),
  maxFiles: z14.number().int().min(1).max(500).optional()
});
var getProjectStructureTool = {
  name: "get_project_structure",
  description: "Get a bounded recursive project structure while ignoring dependency, build, and cache directories.",
  inputSchema: inputSchema14,
  async execute(input, context) {
    try {
      const root = resolveSafePath8(context.workspaceRoot, input.path);
      const ignore = createIgnoreMatcher(await loadIgnoreRules(context.workspaceRoot));
      const structure = await scanProjectStructure(root, { ignore, maxDepth: input.maxDepth, maxFiles: input.maxFiles ?? 100 });
      const output = [
        ...structure.directories.map((path) => `dir	${path}`),
        ...structure.files.map((path) => `file	${path}`)
      ];
      if (structure.truncated) output.push("...	truncated");
      return { ok: true, value: output };
    } catch (error) {
      if (error instanceof Error && error.name.endsWith("Error")) throw error;
      throw new ToolExecutionError13("get_project_structure", error);
    }
  }
};

// src/filesystem/get-project-info.ts
import { buildProjectContext, getCachedProjectContext, setCachedProjectContext } from "@vexalyn/context";
import { ToolExecutionError as ToolExecutionError14 } from "@vexalyn/shared";
import { z as z15 } from "zod";
var getProjectInfoTool = {
  name: "get_project_info",
  description: "Get concise read-only project intelligence: project type, package manager, frameworks, git summary, and important files.",
  inputSchema: z15.object({}),
  async execute(_input, context) {
    try {
      const cached = getCachedProjectContext(context.workspaceRoot);
      const projectContext = cached ?? await buildProjectContext(context.workspaceRoot);
      if (!cached) setCachedProjectContext(context.workspaceRoot, projectContext);
      return { ok: true, value: projectContext.markdown };
    } catch (error) {
      throw new ToolExecutionError14("get_project_info", error);
    }
  }
};

// src/search/search-files.ts
import { readdir as readdir3, readFile as readFile7, stat as stat5 } from "fs/promises";
import { join as join3, relative as relative8 } from "path";
import { resolveSafePath as resolveSafePath9 } from "@vexalyn/context";
import { ToolExecutionError as ToolExecutionError15 } from "@vexalyn/shared";
import { z as z16 } from "zod";
var inputSchema15 = z16.object({
  query: z16.string().min(1),
  path: z16.string().optional().describe('The relative directory or file path to search in. Defaults to "." (workspace root).')
});
var ignored = /* @__PURE__ */ new Set(["node_modules", ".git", "dist", ".turbo", "coverage"]);
async function searchDirectory(root, current, query, results) {
  let entries;
  try {
    entries = await readdir3(current, { withFileTypes: true });
  } catch {
    return;
  }
  for (const entry of entries) {
    if (ignored.has(entry.name)) continue;
    const entryPath = join3(current, entry.name);
    if (entry.isDirectory()) {
      await searchDirectory(root, entryPath, query, results);
      continue;
    }
    if (entry.name.toLowerCase().includes(query.toLowerCase())) {
      results.push(relative8(root, entryPath));
      continue;
    }
    try {
      const content = await readFile7(entryPath, "utf8");
      if (content.includes(query)) results.push(relative8(root, entryPath));
    } catch {
    }
  }
}
async function searchSingleFile(root, filePath, query, results) {
  try {
    const content = await readFile7(filePath, "utf8");
    if (content.includes(query)) results.push(relative8(root, filePath));
  } catch {
  }
}
var searchFilesTool = {
  name: "search_files",
  description: "Recursively search workspace files. Returns paths of files whose name or content matches the query string. This already searches subdirectories, so do not call list_directory before or after it.",
  inputSchema: inputSchema15,
  async execute(input, context) {
    try {
      const root = resolveSafePath9(context.workspaceRoot, input.path ?? ".");
      const results = [];
      const info = await stat5(root);
      if (info.isDirectory()) {
        await searchDirectory(context.workspaceRoot, root, input.query, results);
      } else if (info.isFile()) {
        await searchSingleFile(context.workspaceRoot, root, input.query, results);
      }
      return { ok: true, value: results };
    } catch (error) {
      if (error instanceof Error && error.name.endsWith("Error")) throw error;
      throw new ToolExecutionError15("search_files", error);
    }
  }
};

// src/search/grep.ts
import { readFile as readFile8, stat as stat7 } from "fs/promises";
import { relative as relative9 } from "path";
import { resolveSafePath as resolveSafePath10 } from "@vexalyn/context";
import { ToolExecutionError as ToolExecutionError16 } from "@vexalyn/shared";
import { z as z17 } from "zod";

// src/search/glob-utils.ts
import { readdir as readdir4, stat as stat6 } from "fs/promises";
import { join as join4 } from "path";
var DEFAULT_IGNORED = /* @__PURE__ */ new Set(["node_modules", ".git", "dist", ".turbo", "coverage"]);
function globToRegExp(pattern) {
  let i = 0;
  const len = pattern.length;
  let re = "^";
  while (i < len) {
    const ch = pattern[i];
    if (ch === "*") {
      if (pattern[i + 1] === "*") {
        i += 2;
        if (pattern[i] === "/") i += 1;
        re += "(?:.*)";
      } else {
        re += "(?:[^/]*)";
        i += 1;
      }
      continue;
    }
    if (ch === "?") {
      re += "[^/]";
      i += 1;
      continue;
    }
    if (ch === "[") {
      const end = pattern.indexOf("]", i + 1);
      if (end === -1) {
        re += "\\[";
        i += 1;
        continue;
      }
      let cls = pattern.slice(i, end + 1);
      if (cls.startsWith("[!")) cls = `[^${cls.slice(2)}`;
      re += cls;
      i = end + 1;
      continue;
    }
    if (ch === "{") {
      const end = pattern.indexOf("}", i + 1);
      if (end === -1) {
        re += "\\{";
        i += 1;
        continue;
      }
      const parts = pattern.slice(i + 1, end).split(",");
      re += `(?:${parts.map((p) => p.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`;
      i = end + 1;
      continue;
    }
    re += ch.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    i += 1;
  }
  return new RegExp(`${re}$`);
}
function matchPath(pattern, relPosixPath) {
  return globToRegExp(pattern).test(relPosixPath);
}
async function collectFiles(root, options = {}) {
  const ignored2 = options.ignored ?? DEFAULT_IGNORED;
  const maxFiles = options.maxFiles ?? 2e4;
  const out = [];
  const stack = [root];
  while (stack.length > 0 && out.length < maxFiles) {
    const current = stack.pop();
    let entries;
    try {
      entries = await readdir4(current, { withFileTypes: true });
    } catch {
      continue;
    }
    for (const entry of entries) {
      if (entry.isDirectory()) {
        if (!ignored2.has(entry.name)) stack.push(join4(current, entry.name));
        continue;
      }
      if (!entry.isFile()) continue;
      out.push(join4(current, entry.name));
      if (out.length >= maxFiles) break;
    }
  }
  return out;
}
async function modificationTime(filePath) {
  try {
    const info = await stat6(filePath);
    return info.mtimeMs;
  } catch {
    return 0;
  }
}

// src/search/grep.ts
var inputSchema16 = z17.object({
  pattern: z17.string().min(1).describe('Regular expression to search for inside file contents, e.g. "function\\s+\\w+" or "TODO".'),
  path: z17.string().optional().describe('Relative directory or file path to search in. Defaults to "." (workspace root).'),
  include: z17.string().optional().describe('Glob filter for file names, e.g. "*.ts" or "**/*.{js,ts}". Only files matching this are searched.'),
  ignoreCase: z17.boolean().optional().describe("Case-insensitive matching. Defaults to false.")
});
var MAX_MATCHES = 300;
var MAX_LINE_LENGTH = 240;
var MAX_FILE_BYTES = 2e6;
var grepTool = {
  name: "grep",
  description: 'Search file CONTENTS across the workspace using a regular expression. Returns matches as "path:line: text" entries. Much more precise than search_files for finding code. Use include to filter file types (e.g. "*.ts").',
  inputSchema: inputSchema16,
  async execute(input, context) {
    let regex;
    try {
      regex = new RegExp(input.pattern, input.ignoreCase ? "i" : "");
    } catch (error) {
      return { ok: false, error: `Regex tidak valid: ${error instanceof Error ? error.message : String(error)}` };
    }
    try {
      const root = resolveSafePath10(context.workspaceRoot, input.path ?? ".");
      let candidates = await collectFiles(root);
      if (input.include) {
        candidates = candidates.filter((file) => {
          const rel = relative9(root, file).replace(/\\/g, "/");
          return matchPath(input.include, rel) || matchPath(input.include, rel.split("/").pop());
        });
      }
      const results = [];
      let truncated = false;
      for (const filePath of candidates) {
        if (results.length >= MAX_MATCHES) {
          truncated = true;
          break;
        }
        let content;
        try {
          const info = await stat7(filePath);
          if (info.size > MAX_FILE_BYTES) continue;
          content = await readFile8(filePath, "utf8");
        } catch {
          continue;
        }
        if (content.includes("\0")) continue;
        const lines = content.split("\n");
        for (let i = 0; i < lines.length; i += 1) {
          if (regex.test(lines[i])) {
            const text = lines[i].trim();
            results.push(`${relative9(context.workspaceRoot, filePath).replace(/\\/g, "/")}:${i + 1}: ${text.length > MAX_LINE_LENGTH ? `${text.slice(0, MAX_LINE_LENGTH)}...` : text}`);
            if (results.length >= MAX_MATCHES) {
              truncated = true;
              break;
            }
          }
        }
      }
      if (truncated) results.push(`...(hasil dipotong pada ${MAX_MATCHES} match pertama. Persempit pola, path, atau include untuk melihat lainnya.)`);
      return { ok: true, value: results };
    } catch (error) {
      if (error instanceof Error && error.name.endsWith("Error")) throw error;
      throw new ToolExecutionError16("grep", error);
    }
  }
};

// src/search/glob-files.ts
import { relative as relative10 } from "path";
import { resolveSafePath as resolveSafePath11 } from "@vexalyn/context";
import { ToolExecutionError as ToolExecutionError17 } from "@vexalyn/shared";
import { z as z18 } from "zod";
var inputSchema17 = z18.object({
  pattern: z18.string().min(1).describe('Glob pattern to find files, e.g. "**/*.ts", "src/**/*.test.ts", "*.{json,md}".'),
  path: z18.string().optional().describe('Relative root directory for the search. Defaults to "." (workspace root).')
});
var MAX_RESULTS = 1e3;
var globTool = {
  name: "glob",
  description: 'Find FILES by glob pattern (e.g. "**/*.ts") and return matching paths sorted by most recently modified. Use this when you need file discovery by name/extension; use grep to search inside file contents.',
  inputSchema: inputSchema17,
  async execute(input, context) {
    try {
      const root = resolveSafePath11(context.workspaceRoot, input.path ?? ".");
      const pattern = input.pattern.replace(/\\/g, "/");
      const files = await collectFiles(root);
      const matches = [];
      for (const filePath of files) {
        const relToRoot = relative10(root, filePath).replace(/\\/g, "/");
        const candidate = pattern.includes("/") ? relToRoot : relToRoot.split("/").pop();
        if (matchPath(pattern, candidate)) {
          matches.push({ abs: filePath, rel: relative10(context.workspaceRoot, filePath).replace(/\\/g, "/") });
          if (matches.length >= MAX_RESULTS) break;
        }
      }
      const mtimes = await Promise.all(matches.map((m) => modificationTime(m.abs)));
      const paired = matches.map((m, i) => ({ rel: m.rel, mtime: mtimes[i] ?? 0 }));
      paired.sort((a, b) => b.mtime - a.mtime);
      return { ok: true, value: paired.map((p) => p.rel) };
    } catch (error) {
      if (error instanceof Error && error.name.endsWith("Error")) throw error;
      throw new ToolExecutionError17("glob", error);
    }
  }
};

// src/filesystem/run-command.ts
import { spawn } from "child_process";
import { resolveSafePath as resolveSafePath12 } from "@vexalyn/context";
import {
  CommandCancelledError,
  CommandCwdSecurityError,
  CommandExecutionError,
  CommandNotFoundError,
  CommandPermissionDeniedError,
  CommandValidationError as CommandValidationError2
} from "@vexalyn/shared";
import { z as z19 } from "zod";

// src/command/command-policy.ts
import { basename as basename2 } from "path";
import { CommandValidationError } from "@vexalyn/shared";
var ALLOWED_COMMANDS = /* @__PURE__ */ new Set([
  "node",
  "npm",
  "npx",
  "pnpm",
  "yarn",
  "git",
  "python",
  "python3",
  "pip",
  "pytest",
  "cargo",
  "rustc",
  "go",
  "dotnet",
  "php",
  "composer",
  "echo",
  "cat",
  "ls",
  "dir",
  "type"
]);
function commandExecutable(command) {
  const firstToken = command.trim().match(/^(?:"([^"]+)"|'([^']+)'|([^\s]+))/)?.slice(1).find(Boolean);
  if (!firstToken) throw new CommandValidationError(command);
  return basename2(firstToken).replace(/\.exe$/i, "").toLowerCase();
}
function validateCommand(command) {
  if (!command.trim()) throw new CommandValidationError(command);
  if (/[;&|<>\r\n]/.test(command)) throw new CommandValidationError(command);
  const executable = commandExecutable(command);
  if (!ALLOWED_COMMANDS.has(executable)) throw new CommandValidationError(command);
}
function sanitizeEnv(env) {
  return Object.fromEntries(
    Object.entries(env).filter(([key]) => !/API_KEY|SECRET|TOKEN|PASSWORD|CREDENTIAL|AUTH/i.test(key))
  );
}

// src/filesystem/run-command.ts
var inputSchema18 = z19.object({ command: z19.string().min(1), cwd: z19.string().optional() });
var MAX_OUTPUT = 1e5;
var runCommandTool = {
  name: "run_command",
  description: "Execute an allowlisted terminal command inside the workspace after permission approval.",
  inputSchema: inputSchema18,
  async execute(input, context) {
    validateCommand(input.command);
    let cwd;
    try {
      cwd = resolveSafePath12(context.workspaceRoot, input.cwd ?? ".");
    } catch {
      throw new CommandCwdSecurityError(input.cwd ?? ".");
    }
    if (context.approve && !await context.approve("execute", input.command)) {
      throw new CommandPermissionDeniedError(input.command);
    }
    if (context.signal?.aborted) {
      throw new CommandCancelledError(input.command);
    }
    const startedAt = Date.now();
    try {
      return await new Promise((resolve3) => {
        const child = spawn(input.command, {
          shell: true,
          cwd,
          env: sanitizeEnv(process.env),
          windowsHide: true
        });
        let stdout = "";
        let stderr = "";
        let truncated = false;
        let cancelled = false;
        const append = (current, chunk) => {
          if (current.length >= MAX_OUTPUT) {
            truncated = true;
            return current;
          }
          const text = chunk.toString();
          const remaining = MAX_OUTPUT - current.length;
          if (text.length > remaining) truncated = true;
          return current + text.slice(0, remaining);
        };
        child.stdout.on("data", (chunk) => {
          stdout = append(stdout, chunk);
        });
        child.stderr.on("data", (chunk) => {
          stderr = append(stderr, chunk);
        });
        const onAbort = () => {
          cancelled = true;
          child.kill();
        };
        context.signal?.addEventListener("abort", onAbort, { once: true });
        const detachAbort = () => {
          context.signal?.removeEventListener("abort", onAbort);
        };
        child.on("error", (error) => {
          detachAbort();
          resolve3({ ok: false, error: error.code === "ENOENT" ? new CommandNotFoundError(input.command).message : new CommandExecutionError(input.command, error).message });
        });
        child.on("close", (exitCode, signal) => {
          detachAbort();
          if (cancelled || context.signal?.aborted) {
            resolve3({ ok: false, error: new CommandCancelledError(input.command).message });
            return;
          }
          resolve3({
            ok: exitCode === 0 && !signal,
            value: { command: input.command, cwd, stdout, stderr, exitCode: exitCode ?? 1, signal, durationMs: Date.now() - startedAt, truncated }
          });
        });
      });
    } catch (error) {
      if (error instanceof CommandValidationError2 || error instanceof CommandCwdSecurityError || error instanceof CommandPermissionDeniedError) throw error;
      throw new CommandExecutionError(input.command, error);
    }
  }
};

// src/search/web-search.ts
import { z as z20 } from "zod";
var inputSchema19 = z20.object({
  query: z20.string().describe("The search query to look up on the web.")
});
async function fetchWithTimeout(url, options = {}, timeoutMs = 6e3) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    clearTimeout(id);
    return response;
  } catch (err) {
    clearTimeout(id);
    throw err;
  }
}
var webSearchTool = {
  name: "web_search",
  description: "Search the web using DuckDuckGo/Bing to find information, docs, or libraries.",
  inputSchema: inputSchema19,
  async execute(input) {
    const results = [];
    const userAgent = "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36";
    try {
      const response = await fetchWithTimeout(
        `https://www.bing.com/search?q=${encodeURIComponent(input.query)}`,
        { headers: { "User-Agent": userAgent } },
        6e3
      );
      if (response.ok) {
        const html = await response.text();
        const blocks = html.split('class="b_algo"');
        for (let i = 1; i < blocks.length; i++) {
          if (results.length >= 5) break;
          const block = blocks[i];
          if (!block) continue;
          const titleMatch = block.match(/<h2[^>]*><a[^>]*href="([^"]+)"[^>]*>([\s\S]*?)<\/a><\/h2>/);
          const snippetMatch = block.match(/<p[^>]*>([\s\S]*?)<\/p>/);
          if (titleMatch && titleMatch[1] && titleMatch[2]) {
            let url = titleMatch[1];
            if (url.includes("u=")) {
              const uParam = url.split("u=")[1]?.split("&")[0];
              if (uParam) {
                try {
                  const b64 = uParam.substring(2);
                  url = Buffer.from(b64, "base64").toString("utf8");
                } catch {
                }
              }
            }
            const title = titleMatch[2].replace(/<[^>]*>/g, "").trim();
            const snippetRaw = snippetMatch && snippetMatch[1] ? snippetMatch[1] : "";
            const snippet = snippetRaw.replace(/<[^>]*>/g, "").trim();
            results.push({ title, url, snippet });
          }
        }
        if (results.length > 0) {
          return { ok: true, value: results };
        }
      }
    } catch {
    }
    try {
      const response = await fetchWithTimeout(
        `https://html.duckduckgo.com/html/?q=${encodeURIComponent(input.query)}`,
        { headers: { "User-Agent": userAgent } },
        6e3
      );
      if (response.ok) {
        const html = await response.text();
        const resultBlocks = html.split('<div class="result body');
        for (let i = 1; i < resultBlocks.length; i++) {
          if (results.length >= 5) break;
          const block = resultBlocks[i];
          if (!block) continue;
          const titleMatch = block.match(/<a class="result__a"[^>]*href="([^"]+)"[^>]*>([\s\S]*?)<\/a>/);
          const snippetMatch = block.match(/<a class="result__snippet"[^>]*>([\s\S]*?)<\/a>/);
          if (titleMatch && titleMatch[1] && titleMatch[2]) {
            let url = titleMatch[1];
            if (url.includes("uddg=")) {
              const parts = url.split("uddg=");
              const targetPart = parts[1];
              if (targetPart) {
                const subparts = targetPart.split("&")[0];
                if (subparts) {
                  url = decodeURIComponent(subparts);
                }
              }
            }
            const title = titleMatch[2].replace(/<[^>]*>/g, "").trim();
            const rawSnippet = snippetMatch && snippetMatch[1] ? snippetMatch[1] : "";
            const snippet = rawSnippet.replace(/<[^>]*>/g, "").trim();
            results.push({ title, url, snippet });
          }
        }
        if (results.length > 0) {
          return { ok: true, value: results };
        }
      }
    } catch {
    }
    try {
      const response = await fetchWithTimeout(
        `https://lite.duckduckgo.com/lite/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            "User-Agent": userAgent
          },
          body: `q=${encodeURIComponent(input.query)}`
        },
        6e3
      );
      if (response.ok) {
        const html = await response.text();
        const rows = html.split("<tr");
        let currentTitle = "";
        let currentUrl = "";
        for (let i = 1; i < rows.length; i++) {
          if (results.length >= 5) break;
          const row = rows[i];
          if (!row) continue;
          const linkMatch = row.match(/<a[^>]*href="([^"]+)"[^>]*class="result-link"[^>]*>([\s\S]*?)<\/a>/);
          if (linkMatch && linkMatch[1] && linkMatch[2]) {
            currentUrl = linkMatch[1];
            currentTitle = linkMatch[2].replace(/<[^>]*>/g, "").trim();
            continue;
          }
          const snippetMatch = row.match(/<td[^>]*class="result-snippet"[^>]*>([\s\S]*?)<\/td>/);
          if (snippetMatch && snippetMatch[1] && currentTitle && currentUrl) {
            const snippet = snippetMatch[1].replace(/<[^>]*>/g, "").trim();
            results.push({ title: currentTitle, url: currentUrl, snippet });
            currentTitle = "";
            currentUrl = "";
          }
        }
        if (results.length > 0) {
          return { ok: true, value: results };
        }
      }
    } catch {
    }
    try {
      const response = await fetchWithTimeout(
        `https://api.duckduckgo.com/?q=${encodeURIComponent(input.query)}&format=json&no_html=1`,
        { headers: { "User-Agent": userAgent } },
        6e3
      );
      if (response.ok) {
        const data = await response.json();
        if (data.AbstractText && data.AbstractURL) {
          results.push({
            title: data.AbstractText.substring(0, 60) + "...",
            url: data.AbstractURL,
            snippet: data.AbstractText
          });
        }
        if (data.RelatedTopics) {
          for (const topic of data.RelatedTopics) {
            if (results.length >= 5) break;
            if (topic.Text && topic.FirstURL) {
              results.push({
                title: topic.Text.substring(0, 60) + "...",
                url: topic.FirstURL,
                snippet: topic.Text
              });
            }
          }
        }
        if (results.length > 0) {
          return { ok: true, value: results };
        }
      }
    } catch {
    }
    if (results.length === 0) {
      throw new Error("All DuckDuckGo and Bing search strategies failed due to network issues.");
    }
    return { ok: true, value: results };
  }
};

// src/search/fetch-web-page.ts
import { z as z21 } from "zod";
import { ToolExecutionError as ToolExecutionError18 } from "@vexalyn/shared";
var inputSchema20 = z21.object({
  url: z21.string().describe("The URL of the web page to fetch.")
});
var fetchWebPageTool = {
  name: "fetch_web_page",
  description: "Download the text content of a web page/documentation link.",
  inputSchema: inputSchema20,
  async execute(input) {
    try {
      const response = await fetch(input.url, {
        headers: {
          "User-Agent": "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36",
          "Accept": "text/html,application/xhtml+xml,application/xml;q=0.9,image/webp,*/*;q=0.8"
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const html = await response.text();
      let text = html.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "").replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
      if (text.length > 1e4) {
        text = text.substring(0, 1e4) + "... [content truncated]";
      }
      return { ok: true, value: text };
    } catch (error) {
      throw new ToolExecutionError18("fetch_web_page", error);
    }
  }
};

// src/search/semantic-search.ts
import { readdir as readdir5, readFile as readFile9, stat as stat8 } from "fs/promises";
import { join as join5, relative as relative11 } from "path";
import { resolveSafePath as resolveSafePath13 } from "@vexalyn/context";
import { ToolExecutionError as ToolExecutionError19 } from "@vexalyn/shared";
import { z as z22 } from "zod";
var inputSchema21 = z22.object({
  query: z22.string().describe('The search query describing what you are looking for (e.g. "authentication logic").'),
  path: z22.string().optional().describe('The relative directory path to restrict the search to. Defaults to "." (workspace root).')
});
var IGNORED_DIRS = /* @__PURE__ */ new Set(["node_modules", ".git", "dist", ".turbo", "coverage", ".next", "out", "build"]);
var ALLOWED_EXTENSIONS = /* @__PURE__ */ new Set([".ts", ".tsx", ".js", ".jsx", ".json", ".md", ".css"]);
var SYNONYM_MAP = {
  login: ["auth", "signin", "session", "token", "jwt", "credentials", "password"],
  logout: ["signout", "session", "clear"],
  auth: ["login", "signin", "signup", "session", "jwt", "token", "authenticate", "authorization", "credential"],
  database: ["db", "prisma", "sql", "schema", "model", "postgres", "mysql", "mongodb", "query", "save", "store"],
  route: ["controller", "endpoint", "api", "url", "path", "router", "get", "post"],
  command: ["execute", "run", "spawn", "exec", "shell", "cli"],
  search: ["find", "grep", "filter", "query", "locate", "websearch"],
  config: ["env", "settings", "options", "setup", "loadconfig"],
  file: ["read", "write", "edit", "delete", "filesystem", "path"]
};
function tokenize(text) {
  return text.replace(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase().replace(/[^a-z0-9\s]/g, " ").split(/\s+/).filter((t) => t.length > 1);
}
async function collectFiles2(current, files) {
  let entries;
  try {
    entries = await readdir5(current, { withFileTypes: true });
  } catch {
    return;
  }
  for (const entry of entries) {
    const entryPath = join5(current, entry.name);
    if (entry.isDirectory()) {
      if (IGNORED_DIRS.has(entry.name)) continue;
      await collectFiles2(entryPath, files);
    } else {
      const ext = entry.name.substring(entry.name.lastIndexOf(".")).toLowerCase();
      if (ALLOWED_EXTENSIONS.has(ext)) {
        files.push(entryPath);
      }
    }
  }
}
var semanticSearchTool = {
  name: "semantic_search",
  description: 'Search workspace files semantically. Understands synonyms (e.g. "login" matches "auth", "session") and ranks results by conceptual relevance. Use this when search_files fails to locate conceptual logic.',
  inputSchema: inputSchema21,
  async execute(input, context) {
    try {
      const searchRoot = resolveSafePath13(context.workspaceRoot, input.path ?? ".");
      const filePaths = [];
      const info = await stat8(searchRoot);
      if (info.isDirectory()) {
        await collectFiles2(searchRoot, filePaths);
      } else if (info.isFile()) {
        filePaths.push(searchRoot);
      }
      const queryTokens = tokenize(input.query);
      const expandedQueryTokens = /* @__PURE__ */ new Set();
      for (const token of queryTokens) {
        expandedQueryTokens.add(token);
        const synonyms = SYNONYM_MAP[token];
        if (synonyms) {
          for (const syn of synonyms) {
            expandedQueryTokens.add(syn);
          }
        }
      }
      const results = [];
      for (const filePath of filePaths) {
        let content = "";
        try {
          content = await readFile9(filePath, "utf8");
        } catch {
          continue;
        }
        const relPath = relative11(context.workspaceRoot, filePath).replace(/\\/g, "/");
        const pathLower = relPath.toLowerCase();
        let matches = 0;
        let score = 0;
        let snippet = "";
        const lines = content.split("\n");
        const docTokens = tokenize(content);
        const docTokenSet = new Set(docTokens);
        for (const token of expandedQueryTokens) {
          if (pathLower.includes(token)) {
            score += 10;
            matches++;
          }
          if (docTokenSet.has(token)) {
            const occurrences = docTokens.filter((t) => t === token).length;
            score += Math.min(occurrences * 0.5, 5);
            matches++;
            if (!snippet) {
              const matchingLine = lines.find((line) => line.toLowerCase().includes(token));
              if (matchingLine) {
                snippet = matchingLine.trim();
              }
            }
          }
        }
        if (matches > 0 && score > 0) {
          const lengthPenalty = Math.log10(docTokens.length + 10);
          const finalScore = Number((score / lengthPenalty).toFixed(2));
          results.push({
            path: relPath,
            score: finalScore,
            snippet: snippet || (lines[0] ? lines[0].trim() : "")
          });
        }
      }
      const sortedResults = results.sort((a, b) => b.score - a.score).slice(0, 10);
      return { ok: true, value: sortedResults };
    } catch (error) {
      if (error instanceof Error && error.name.endsWith("Error")) throw error;
      throw new ToolExecutionError19("semantic_search", error);
    }
  }
};

// src/filesystem/git.ts
import { execFile } from "child_process";
import { promisify as promisify2 } from "util";
import { ToolExecutionError as ToolExecutionError20 } from "@vexalyn/shared";
import { z as z23 } from "zod";
var execFileAsync = promisify2(execFile);
var GIT_MAX_BUFFER = 10 * 1024 * 1024;
var gitStatusTool = {
  name: "git_status",
  description: "Get the status of the git repository (modified, untracked, staged files) without requiring execution approval.",
  inputSchema: z23.object({}),
  async execute(_input, context) {
    try {
      const { stdout } = await execFileAsync("git", ["status", "--porcelain"], { cwd: context.workspaceRoot, maxBuffer: GIT_MAX_BUFFER });
      return { ok: true, value: stdout.trim() || "Repository is clean (no modified or untracked files)." };
    } catch (error) {
      throw new ToolExecutionError20("git_status", error);
    }
  }
};
var gitDiffTool = {
  name: "git_diff",
  description: "Get the current unstaged/staged git changes in the workspace without requiring execution approval.",
  inputSchema: z23.object({
    path: z23.string().optional().describe("Optional file path to filter diff for a specific file.")
  }),
  async execute(input, context) {
    try {
      const args = input.path ? ["diff", "--", input.path] : ["diff"];
      const { stdout } = await execFileAsync("git", args, { cwd: context.workspaceRoot, maxBuffer: GIT_MAX_BUFFER });
      return { ok: true, value: stdout.trim() || "No git diff changes." };
    } catch (error) {
      throw new ToolExecutionError20("git_diff", error);
    }
  }
};

// src/interactive/todo.ts
import { z as z24 } from "zod";
var MAX_TODOS = 50;
var todoStore = [];
var nextId = 1;
function getPriorityWeight(p) {
  return p === "high" ? 0 : p === "medium" ? 1 : 2;
}
function sortTodos(items) {
  return [...items].sort((a, b) => {
    const statusOrder = {
      in_progress: 0,
      pending: 1,
      completed: 2,
      cancelled: 3
    };
    const s = statusOrder[a.status] - statusOrder[b.status];
    if (s !== 0) return s;
    return getPriorityWeight(a.priority) - getPriorityWeight(b.priority);
  });
}
var todoWriteInput = z24.object({
  todos: z24.array(z24.object({
    content: z24.string().min(1).describe("Brief description of the task."),
    status: z24.enum(["pending", "in_progress", "completed", "cancelled"]).describe("Current status of the task."),
    priority: z24.enum(["high", "medium", "low"]).default("medium").describe("Priority level of the task."),
    id: z24.number().int().positive().optional().describe("Existing todo ID to update. Omit for new items.")
  })).min(1).max(MAX_TODOS).describe("The full todo list. This REPLACES the entire list.")
});
var todoWriteTool = {
  name: "todowrite",
  description: "Create and maintain a structured task list for the current coding session. Tracks progress, organizes multi-step work, and surfaces status to the user. Pass the FULL list each time \u2014 this replaces the entire todo list. Use proactively when the task requires 3+ distinct steps, when the user provides multiple tasks, or when explicitly asked for a todo list. Skip for single straightforward tasks.",
  inputSchema: todoWriteInput,
  async execute(input) {
    const incoming = input.todos;
    const preserved = [];
    const created = [];
    for (const item of incoming) {
      if (item.id !== void 0) {
        const existing = todoStore.find((t) => t.id === item.id);
        if (existing) {
          existing.content = item.content;
          existing.status = item.status;
          existing.priority = item.priority;
          preserved.push(existing);
          continue;
        }
      }
      const newTodo = {
        id: nextId++,
        content: item.content,
        status: item.status,
        priority: item.priority
      };
      todoStore.push(newTodo);
      created.push(newTodo);
    }
    const incomingIds = new Set(incoming.map((t) => t.id).filter((id) => id !== void 0));
    for (let i = todoStore.length - 1; i >= 0; i--) {
      const todo = todoStore[i];
      if (incomingIds.size > 0 && !incomingIds.has(todo.id) && !created.includes(todo)) {
        todoStore.splice(i, 1);
      }
    }
    const sorted = sortTodos(todoStore);
    const inProgress = sorted.filter((t) => t.status === "in_progress");
    if (inProgress.length > 1) {
      for (let i = 1; i < inProgress.length; i++) {
        inProgress[i].status = "pending";
      }
    }
    return {
      ok: true,
      value: { todos: sorted }
    };
  }
};
var todoReadInput = z24.object({
  status: z24.enum(["pending", "in_progress", "completed", "cancelled"]).optional().describe("Filter by status. Omit to read all todos.")
});
var todoReadTool = {
  name: "todoread",
  description: "Read the current todo list. Returns all todos with their content, status, and priority. Optionally filter by status (e.g. only pending, only in_progress). Use to check what work remains before starting new tasks.",
  inputSchema: todoReadInput,
  async execute(input) {
    let result = todoStore;
    if (input.status) {
      result = result.filter((t) => t.status === input.status);
    }
    return {
      ok: true,
      value: { todos: sortTodos(result) }
    };
  }
};
function getAllTodos() {
  return sortTodos(todoStore);
}
function clearTodos() {
  todoStore.length = 0;
  nextId = 1;
}
function getTodoSummary() {
  return {
    total: todoStore.length,
    pending: todoStore.filter((t) => t.status === "pending").length,
    inProgress: todoStore.filter((t) => t.status === "in_progress").length,
    completed: todoStore.filter((t) => t.status === "completed").length,
    cancelled: todoStore.filter((t) => t.status === "cancelled").length
  };
}

// src/interactive/task.ts
import { z as z25 } from "zod";
var EXPLORE_PROMPT = `You are an exploration subagent. Your job is to explore the codebase and answer questions.
You have access to read-only tools: read_file, list_directory, search_files, grep, glob, get_project_structure, get_code_outline, get_project_info, git_status, git_diff, lsp, semantic_search.
DO NOT modify any files. DO NOT run commands that change state. Only read and report.
Be thorough: search across multiple directories, read relevant files, and provide a comprehensive answer.
Return a clear, detailed summary of your findings. Include file paths and line numbers when referencing code.`;
var GENERAL_PROMPT = `You are a general-purpose subagent. You handle complex, multi-step tasks autonomously.
You have access to all tools. Work through the task methodically:
1. Understand what's being asked
2. Explore the codebase if needed
3. Plan your approach
4. Execute the work
5. Verify your changes
Return a clear summary of what you did and any important findings. Include file paths when referencing code.`;
function getSubagentPrompt(type) {
  return type === "explore" ? EXPLORE_PROMPT : GENERAL_PROMPT;
}
var taskInput = z25.object({
  description: z25.string().min(1).max(100).describe("A short 3-5 word description of the task."),
  prompt: z25.string().min(1).describe("The detailed task for the subagent to perform."),
  subagent_type: z25.enum(["explore", "general"]).default("general").describe('Type of subagent: "explore" for codebase exploration (read-only), "general" for multi-step tasks.'),
  command: z25.string().optional().describe("The command that triggered this task. For logging only.")
});
function createTaskTool(deps) {
  return {
    name: "task",
    description: 'Launch a new agent to handle complex, multistep tasks autonomously.\n\nWhen NOT to use this tool:\n- If you want to read a specific file, use the Read or Glob tool instead\n- If you are searching for a specific class definition like "class Foo", use the Grep tool instead\n- If you are searching within 2-3 files, use the Read tool instead\n\nWhen to use this tool:\n- When you need to do multiple rounds of searching and reading\n- When you need to perform complex multi-step tasks that would fill up your context\n- When the task can be done in parallel\n\nUse "explore" for codebase research (read-only) and "general" for tasks that need full tool access.\nOnce you launch a task, do NOT duplicate that work yourself. Wait for the result and use it.',
    inputSchema: taskInput,
    async execute(input) {
      const result = await deps.runSubagent(input.prompt, input.subagent_type, input.description);
      return {
        ok: true,
        value: { result }
      };
    }
  };
}

// src/index.ts
function createFilesystemToolRegistry(planManager) {
  const registry = new ToolRegistry().register(getCodeOutlineTool).register(listDirectoryTool).register(readFileTool).register(writeFileTool).register(editFileTool).register(applyPatchTool).register(questionTool).register(lspTool).register(skillTool).register(deleteFileTool).register(createDirectoryTool).register(deleteDirectoryTool).register(getProjectInfoTool).register(getProjectStructureTool).register(searchFilesTool).register(grepTool).register(globTool).register(runCommandTool).register(webSearchTool).register(fetchWebPageTool).register(semanticSearchTool).register(gitStatusTool).register(gitDiffTool).register(todoWriteTool).register(todoReadTool);
  if (planManager) {
    registry.register(createPlanTool(planManager));
  }
  return registry;
}
export {
  PermissionManager,
  PlanManager,
  ToolRegistry,
  applyPatchTool,
  clearTodos,
  computeUnifiedDiff,
  createDirectoryTool,
  createFilesystemToolRegistry,
  createPlanTool,
  createTaskTool,
  deleteDirectoryTool,
  deleteFileTool,
  diffStats,
  editFileTool,
  fetchWebPageTool,
  formatDiffText,
  getAllTodos,
  getCodeOutlineTool,
  getProjectInfoTool,
  getProjectStructureTool,
  getSubagentPrompt,
  getTodoSummary,
  gitDiffTool,
  gitStatusTool,
  globTool,
  grepTool,
  listDirectoryTool,
  listSkills,
  lspTool,
  questionTool,
  readFileTool,
  runCommandTool,
  searchFilesTool,
  semanticSearchTool,
  skillDirectories,
  skillTool,
  todoReadTool,
  todoWriteTool,
  webSearchTool,
  writeFileTool
};
